import React from 'react';
import { Sparkles, Users, ArrowLeft, AlertTriangle } from 'lucide-react';
import { sommeEffectifsCoches, classesEnBaseHorsFichier } from '../../utils/importEtablissement';

/**
 * Étape 2 : sélection du périmètre par CLASSES + compteur licence (spec §10.1bis).
 * Principe UI (revu 21/07) : la coche n'est qu'un interrupteur — c'est la ligne
 * de VERDICT sous chaque classe qui porte le sens (mise à jour / créée /
 * retirée / ignorée), et elle réagit en direct. Un résumé vivant au-dessus du
 * CTA rappelle que la vérification ne modifie rien.
 */
function EtapeClasses({ groupes, cochees, sansClasse = 0, onToggle, onTout, listClasses, limite, onAnalyser, onRetour, chargement }) {
    const totalCoche = sommeEffectifsCoches(groupes, cochees);
    const depassement = limite !== null && limite !== undefined && totalCoche > limite;
    const classesAbsentes = classesEnBaseHorsFichier(groupes, listClasses);

    // Résumé vivant : ce que la vérification fera avec la sélection actuelle
    const nbMaj = groupes.filter((g) => cochees.has(g.cle) && g.classeId !== null).length;
    const nbCreees = groupes.filter((g) => cochees.has(g.cle) && g.classeId === null).length;
    const nbRetirees = groupes.filter((g) => !cochees.has(g.cle) && g.nombreActuel > 0).length
        + classesAbsentes.length;

    // Verdict d'une ligne (le cœur de l'écran)
    const verdict = (g, cochee) => {
        if (cochee && g.classeId !== null) {
            return { texte: '→ Sera mise à jour (les changements seront détectés à la vérification)', classe: 'text-blue-700' };
        }
        if (cochee) {
            return { texte: `→ Sera créée avec ${g.lignes.length} élève${g.lignes.length > 1 ? 's' : ''}`, classe: 'text-violet-700' };
        }
        if (g.nombreActuel > 0) {
            return {
                texte: `⚠️ Ne sera plus dans TanoEd : ses ${g.nombreActuel} élève${g.nombreActuel > 1 ? 's' : ''} seront considérés comme partis`,
                classe: 'text-red-600 font-medium',
            };
        }
        return { texte: 'Ignorée : rien ne changera', classe: 'text-slate-400' };
    };

    return (
        <div>
            {/* Consigne binaire */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 mb-4 space-y-1">
                <p className="text-sm text-slate-700">
                    ✅ <span className="font-semibold">Coché</span> = cette classe fait partie de l'établissement →
                    elle sera <span className="font-semibold text-blue-700">mise à jour</span> (ou
                    <span className="font-semibold text-violet-700"> créée</span>).
                </p>
                <p className="text-sm text-slate-700">
                    ⬜ <span className="font-semibold">Décoché</span> = cette classe
                    <span className="font-semibold text-red-600"> n'existe plus</span> ou ne fait pas partie de
                    votre licence → elle ne sera pas (ou plus) dans TanoEd.
                </p>
            </div>

            {/* Compteur licence + tout cocher */}
            <div className="flex flex-wrap items-center justify-end gap-3 mb-3">
                <div className={`px-4 py-2 rounded-xl border text-sm font-semibold ${
                    depassement
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : 'bg-blue-50 border-blue-200 text-blue-800'
                }`}>
                    <Users className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
                    {limite !== null && limite !== undefined
                        ? `${totalCoche} / ${limite} élèves`
                        : `${totalCoche} élèves`}
                </div>
                <div className="flex gap-1">
                    <button
                        onClick={() => onTout(true)}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1.5 rounded-lg transition-colors"
                    >
                        Tout cocher
                    </button>
                    <button
                        onClick={() => onTout(false)}
                        className="text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-2 py-1.5 rounded-lg transition-colors"
                    >
                        Tout décocher
                    </button>
                </div>
            </div>

            {depassement ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-800">
                    ⚠️ La sélection dépasse la limite d'élèves de votre licence ({limite}). Décochez des classes
                    ou contactez TanoEd pour faire évoluer la licence.
                </div>
            ) : null}

            {/* Lignes sans classe : ignorées, dites une fois (on n'importe que
                les élèves qui ont une classe) */}
            {sansClasse > 0 ? (
                <p className="text-xs text-slate-500 mb-3">
                    {sansClasse} élève{sansClasse > 1 ? 's' : ''} sans classe dans votre fichier : ignoré{sansClasse > 1 ? 's' : ''}.
                </p>
            ) : null}

            {/* Liste des classes : coche + nombres étiquetés + VERDICT */}
            <div className="space-y-2 mb-5">
                {groupes.map((g) => {
                    const cochee = cochees.has(g.cle);
                    const v = verdict(g, cochee);
                    return (
                        <label
                            key={g.cle}
                            className={`block py-2.5 px-4 rounded-xl border cursor-pointer transition-all ${
                                cochee
                                    ? 'bg-blue-50/60 border-blue-200'
                                    : g.nombreActuel > 0
                                        ? 'bg-red-50/40 border-red-200'
                                        : 'bg-white border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={cochee}
                                    onChange={() => onToggle(g.cle)}
                                    className="w-4 h-4 accent-blue-600 flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <span className="font-semibold text-gray-800 text-sm">{g.nomFichier}</span>
                                    {g.classeId === null ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[11px] font-medium">
                                            <Sparkles className="w-3 h-3" />
                                            nouvelle
                                        </span>
                                    ) : null}
                                    {g.nomReconnu && g.nomReconnu !== g.nomFichier ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-medium">
                                            → {g.nomReconnu}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="text-right flex-shrink-0 text-xs text-slate-500">
                                    Dans TanoEd : <span className="font-semibold text-slate-700">{g.classeId !== null ? g.nombreActuel : '-'}</span>
                                    {' · '}
                                    Dans votre fichier : <span className="font-semibold text-blue-700">{g.lignes.length}</span>
                                </div>
                            </div>
                            <p className={`text-xs mt-1 ml-7 ${v.classe}`}>{v.texte}</p>
                        </label>
                    );
                })}
            </div>

            {/* Classes de TanoEd absentes du fichier */}
            {classesAbsentes.length > 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        Classes absentes de votre fichier
                    </p>
                    <p className="text-xs text-amber-800 mb-2">
                        Ces classes ont des élèves dans TanoEd, mais elles ne sont pas dans votre fichier.
                        Si vous continuez, elles <span className="font-semibold">ne seront plus dans TanoEd</span> :
                        leurs élèves seront considérés comme partis. Si ce n'est pas voulu, refaites l'export
                        Pronote avec toutes les classes.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {classesAbsentes.map((c) => (
                            <span key={c.id} className="px-2 py-0.5 rounded-lg bg-white border border-amber-200 text-xs text-gray-700">
                                {c.nom} · {c.nombre_eleves} élève{c.nombre_eleves > 1 ? 's' : ''}
                            </span>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* Résumé vivant : ce que la vérification va faire (et ne PAS faire) */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 mb-4">
                <p className="text-sm text-blue-900">
                    ➜ La vérification comparera votre fichier avec TanoEd :{' '}
                    <span className="font-semibold">{nbMaj} classe{nbMaj > 1 ? 's' : ''} mise{nbMaj > 1 ? 's' : ''} à jour</span>
                    {' · '}
                    <span className="font-semibold text-violet-700">{nbCreees} créée{nbCreees > 1 ? 's' : ''}</span>
                    {' · '}
                    <span className={`font-semibold ${nbRetirees > 0 ? 'text-red-600' : ''}`}>{nbRetirees} retirée{nbRetirees > 1 ? 's' : ''}</span>.
                </p>
                <p className="text-xs text-blue-800 mt-1">
                    Rien n'est modifié à cette étape. Vous verrez d'abord le détail des changements et rien ne
                    sera enregistré sans votre validation.
                </p>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center gap-3">
                <button
                    onClick={onRetour}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-3 py-2 rounded-xl transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Changer de fichier
                </button>
                <button
                    onClick={onAnalyser}
                    disabled={chargement || depassement || totalCoche === 0}
                    className={`px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all ${
                        chargement || depassement || totalCoche === 0
                            ? 'bg-slate-300 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-blue-500/30'
                    }`}
                >
                    {chargement ? 'Vérification en cours...' : `Vérifier le fichier (${totalCoche} élèves)`}
                </button>
            </div>
        </div>
    );
}

export default EtapeClasses;
